import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import {
  loadPath, reviseAt, savePath, stepsLeft, walkConversation, type Choice, type ScriptResult, type ThreadItem,
} from '../../../../lib/conversation'
import type { ScriptExecutionElementDto } from '../../../../types/canvasser'
import type { ScriptLanguage } from '../../../../lib/walkPrefs'
import { StepCard } from './StepCard'
import { ThreadLine } from './ThreadLine'
import { ConversationProgress } from './ConversationProgress'

/** The tapped answer shows this long before the next step appears. */
const HIGHLIGHT_MS = 150
/** Taps are ignored for this long after a choice, so a double tap can't answer the NEXT step too. */
const LOCK_MS = 400
const VISIBLE_FINISHED = 3

type StepItem = Extract<ThreadItem, { type: 'step' }>

/**
 * [Spec §4.6, Phase 5] The running conversation. State = the saved path (lib/conversation); the thread,
 * the current step, answers and auto-tags are all derived from it. No swipe navigation.
 */
export function ConversationEngine({ elements, stateKey, language, onFinish, onEndEarly }: {
  elements: ScriptExecutionElementDto[]; stateKey: string; language: ScriptLanguage
  onFinish: (r: ScriptResult) => void; onEndEarly: (r: ScriptResult) => void
}) {
  const [path, setPath] = useState<Choice[]>(() => loadPath(stateKey))
  const [editing, setEditing] = useState<number | null>(null)
  const [pending, setPending] = useState<Choice | null>(null)
  const [showEarlier, setShowEarlier] = useState(false)
  const lockedRef = useRef(false)
  const timers = useRef<number[]>([])
  useEffect(() => () => timers.current.forEach(t => window.clearTimeout(t)), [])

  const walk = useMemo(() => walkConversation(elements, path), [elements, path])
  const pathRef = useRef(path)
  pathRef.current = path
  const onFinishRef = useRef(onFinish)
  onFinishRef.current = onFinish

  // A saved path the script no longer fits (the script changed since) is trimmed to what still fits.
  useEffect(() => { if (walk.consumed < path.length) setPath(p => p.slice(0, walk.consumed)) }, [walk.consumed, path.length])
  useEffect(() => { savePath(stateKey, path) }, [stateKey, path])
  // Nothing to ask at all (e.g. a tags-only script): straight to How did it end?
  useEffect(() => {
    if (pathRef.current.length > 0) return
    const w = walkConversation(elements, [])
    if (!w.current) onFinishRef.current(w.results)
  }, [elements])

  const choose = useCallback((c: Choice) => {
    if (lockedRef.current) return
    lockedRef.current = true
    setPending(c)
    const editingAt = editing
    timers.current.push(window.setTimeout(() => {
      const before = pathRef.current
      const next = editingAt === null ? [...before, c] : reviseAt(before, editingAt, c)
      // [PF-2] Persist now: the parent may unmount this engine inside onFinish, before the save effect runs.
      savePath(stateKey, next)
      setPath(next)
      setEditing(null)
      setPending(null)
      if (next !== before) {
        const after = walkConversation(elements, next)
        if (!after.current) onFinishRef.current(after.results)
      }
      timers.current.push(window.setTimeout(() => { lockedRef.current = false }, LOCK_MS - HIGHLIGHT_MS))
    }, HIGHLIGHT_MS))
  }, [editing, elements, stateKey])

  const steps = walk.thread.filter((t): t is StepItem => t.type === 'step')
  // While re-answering a step (P5-1), the thread stops just before it and that step is the card.
  const editItem = editing === null ? null : steps.find(s => s.pathIndex === editing) ?? null
  const shown = editItem ? walk.thread.slice(0, walk.thread.indexOf(editItem)) : walk.thread
  const later = editItem ? steps.filter(s => s.pathIndex > editItem.pathIndex).length : 0
  const card = editItem ? editItem.step : walk.current
  const selected = pending ?? (editItem ? editItem.choice : null)

  // [P5-10] Once 4+ steps are finished, all but the latest 3 fold into "N earlier steps".
  const shownSteps = shown.filter((t): t is StepItem => t.type === 'step')
  const fold = !showEarlier && shownSteps.length >= 4 ? shownSteps.length - VISIBLE_FINISHED : 0
  const visible = fold > 0 ? shown.slice(shown.indexOf(shownSteps[fold - 1]) + 1) : shown
  const notice = editItem && later > 0
    ? `Changing this answer clears the ${later === 1 ? '1 step' : `${later} steps`} after it.` : null

  return (
    <Flex direction="column" flex={1} minH={0}>
      <ConversationProgress done={steps.length} left={stepsLeft(elements, walk)} />
      <VStack flex={1} minH={0} overflowY="auto" align="stretch" gap={2} px={4} py={3}>
        {fold > 0 && (
          <Button variant="ghost" minH="44px" justifyContent="flex-start" color="fg.muted" onClick={() => setShowEarlier(true)}>
            {fold === 1 ? '1 earlier step' : `${fold} earlier steps`}
          </Button>
        )}
        {visible.map(t => (
          <ThreadLine key={t.type === 'step' ? `s-${t.step.id}` : `t-${t.tagId}`} item={t} language={language}
            onChange={t.type === 'step' && t.step.kind !== 'say' && editing === null && pending === null ? () => { if (!lockedRef.current) setEditing(t.pathIndex) } : undefined} />
        ))}
        {card ? (
          <Box mt="auto">
            <StepCard key={card.id} step={card} language={language} selected={selected} onChoose={choose}
              notice={notice} onCancel={editItem ? () => setEditing(null) : undefined} />
          </Box>
        ) : (
          <VStack mt="auto" align="stretch" gap={2} p={4} borderWidth="1px" borderColor="border" borderRadius="md" bg="bg.subtle">
            <Text fontWeight="semibold">End of the script</Text>
            <Button minH="52px" bg="accent" color="accent.fg" onClick={() => onFinish(walk.results)}>How did it end?</Button>
          </VStack>
        )}
      </VStack>
      <HStack px={4} pt={3} pb="calc(12px + env(safe-area-inset-bottom, 0px))" gap={2} borderTopWidth="1px" borderColor="border" bg="surface">
        {card && card.kind !== 'say' && (
          <Button flex={1} minH="48px" variant="outline" borderColor="border" disabled={pending !== null} onClick={() => choose({ stepId: card.id, kind: 'skip' })}>Skip</Button>
        )}
        <Button flex={1} minH="48px" variant="outline" borderColor="border" disabled={pending !== null} onClick={() => { if (!lockedRef.current) onEndEarly(walk.results) }}>End conversation</Button>
      </HStack>
    </Flex>
  )
}
